// The UI scale (R1556, ui-map-interface.md §13 "The UI scale"): the one
// setting that scales the chrome to 100, 120 or 140 %.
//
// **The method is CSS `zoom` on the three chrome roots.** The page writes
// the factor as `--ui-scale` on `<html>`, and `#ui`, `#hub` and `#landing`
// each take `zoom: var(--ui-scale, 1)`. The text and the boxes grow
// together, so no box overflows its text, and no font size changes in a
// stylesheet. The map canvas stands outside the three roots and keeps its
// size; so do the map text and the minimap's own drawing scale.
//
// **Two coordinate spaces meet here.** Inside a root, a length the code
// writes (`style.left`, a custom property) is in CHROME px, and the screen
// in chrome px is the screen in viewport px divided by the factor. A
// pointer's `clientX`,
// `innerWidth` and a canvas position are in VIEWPORT px. A client rect of
// an element inside a root is in viewport px where the browser follows the
// standard (Chrome 128, Firefox 126, a WebKit of 2026), and in chrome px
// in an older WebKit, which divides the zoom out (WebKit bug 77998). `chromeRect` and
// `viewportRect` therefore ask the browser once which of the two it does
// (`probeRects`), and every read of a chrome element's rect goes through
// one of them. `offsetWidth`, `clientWidth` and `scrollTop` are chrome px
// in every browser, so they need no helper.
//
// **The game page, the arcade page and the replay page scale their
// chrome** (R1576, R1579). `enableUiScale` turns the module on at boot,
// and a page that never calls it (the theme workshop and the labs) keeps
// the factor 1 whatever the store holds, so a write from the store there
// changes nothing. The arcade page's and the replay page's one root is
// `#ui`.

import { CHROME_UI_SCALE_DEFAULT, CHROME_UI_SCALE_STEPS } from '../gen/presentation';

/** The steps a player can pick, in percent, in the row order. */
export const UI_SCALE_STEPS: readonly number[] = CHROME_UI_SCALE_STEPS;
/** The step a store with no valid value takes. */
export const UI_SCALE_DEFAULT: number = CHROME_UI_SCALE_DEFAULT;

/** A stored value as a step: a step passes, and anything else is the
 *  default. A value between two steps is not snapped to the nearer one,
 *  because no client ever wrote one. */
export function snapUiScale(raw: unknown): number {
  return typeof raw === 'number' && UI_SCALE_STEPS.includes(raw) ? raw : UI_SCALE_DEFAULT;
}

/** A rectangle, the fields of a `DOMRect` that a placement reads. */
export interface Rect {
  left: number; top: number; right: number; bottom: number; width: number; height: number;
}

/** Does this page scale its chrome? Only `enableUiScale` turns it on. */
let on = false;
/** The factor in force on the three roots. */
let zoom = 1;
/** Does this browser return a chrome element's client rect in viewport
 *  px? null until the first read at a factor other than 1 asks. */
let rectsScaled: boolean | null = null;
const listeners: (() => void)[] = [];

/**
 * Turns the UI scale on for this page and applies `pct`. The game page,
 * the arcade page and the replay page call it once, at boot, before the
 * first chrome stands.
 */
export function enableUiScale(pct: number): void {
  on = true;
  setUiScale(pct);
}

/**
 * Applies one step. It does nothing on a page that did not enable the
 * scale, and nothing when the step is the one in force. A change tells
 * every `onUiScale` listener, because a placement computed in chrome px is
 * stale at a new factor.
 */
export function setUiScale(pct: number): void {
  if (!on) return;
  const z = snapUiScale(pct) / 100;
  if (z === zoom) return;
  zoom = z;
  if (typeof document !== 'undefined') {
    const root = document.documentElement.style;
    if (z === 1) root.removeProperty('--ui-scale');
    else root.setProperty('--ui-scale', String(z));
  }
  for (const fn of listeners) fn();
}

/** Calls `fn` after each change of the factor. */
export function onUiScale(fn: () => void): void { listeners.push(fn); }

/** The factor in force: 1, or the step divided by 100. */
export function uiZoom(): number { return zoom; }

/** A length in viewport px (a pointer, `innerWidth`) as chrome px. */
export function toChromePx(v: number): number { return v / zoom; }

/** The viewport's width and height in chrome px. */
export function chromeViewport(): { w: number; h: number } {
  if (typeof window === 'undefined') return { w: 1280, h: 800 };
  return { w: toChromePx(window.innerWidth), h: toChromePx(window.innerHeight) };
}

/** The factor a client rect of a chrome element carries in this browser:
 *  the zoom where it follows the standard, 1 where it divides the zoom
 *  out. */
function rectZoom(): number {
  if (zoom === 1) return 1;
  if (rectsScaled === null) rectsScaled = probeRects();
  return rectsScaled ? zoom : 1;
}

/**
 * Asks the browser once how it reports a rect inside a zoomed box: a box
 * at zoom 2 holds a child 50 chrome px from its left edge, and a browser
 * that follows the standard puts the child 100 px from the viewport's
 * edge. The box is hidden and leaves the page at once.
 */
function probeRects(): boolean {
  if (typeof document === 'undefined' || !document.body) return true;
  const box = document.createElement('div');
  box.style.cssText = 'position:fixed;left:0;top:0;zoom:2;visibility:hidden;pointer-events:none';
  const child = document.createElement('div');
  child.style.cssText = 'position:absolute;left:50px;top:0;width:1px;height:1px';
  box.appendChild(child);
  document.body.appendChild(box);
  const x = child.getBoundingClientRect().left;
  box.remove();
  return x > 75;
}

/** One rect with every field divided by `k`. */
function scaled(r: Rect, k: number): Rect {
  return {
    left: r.left / k, top: r.top / k, right: r.right / k, bottom: r.bottom / k,
    width: r.width / k, height: r.height / k,
  };
}

/** The client rect of an element inside a chrome root, in chrome px: the
 *  space a `style.left` or a custom property of the chrome is written in. */
export function chromeRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  const k = rectZoom();
  return k === 1 ? r : scaled(r, k);
}

/** The client rect of an element inside a chrome root, in viewport px:
 *  the space of the canvas, the camera and a pointer. */
export function viewportRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  const k = rectZoom() / zoom;
  return k === 1 ? r : scaled(r, k);
}

/** Forgets the module state. Tests only. `scaledRects` stands in for the
 *  probe, so a test picks the browser model it runs. */
export function resetUiScale(scaledRects: boolean | null = null): void {
  on = false;
  zoom = 1;
  rectsScaled = scaledRects;
  listeners.length = 0;
  if (typeof document !== 'undefined') document.documentElement.style.removeProperty('--ui-scale');
}
